import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useOffline, SYNC_STATUS } from "@/context/OfflineContext";
import { 
  LayoutDashboard, 
  Package, 
  Truck, 
  History, 
  FileText,
  Layers,
  LogOut,
  Shield,
  User,
  Wifi,
  WifiOff,
  RefreshCw,
  CloudOff,
  Check,
  AlertTriangle,
  Cloud,
  RotateCcw,
  AlertOctagon,
  ArrowRightLeft,
  Database,
  Scale,
  Factory,
  ChevronDown,
  ChevronRight,
  Boxes,
  ClipboardList,
  BarChart3,
  TrendingUp,
  Menu,
  X,
  PanelLeftClose,
  PanelLeft,
  Users,
  Key,
  Tag,
  TrendingUp as TrendingUpIcon,
  Brain
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import pfLogo from "@/assets/pf-logo.png";

/**
 * ParamOS branded wordmark — matches the login page.
 * "Param" in PF blue + "OS" larger in PF grey, tight serif kerning.
 */
const ParamOSWordmark = ({ size = "18px" }) => (
  <span
    className="font-medium"
    style={{
      fontFamily: "'Playfair Display', 'DM Serif Display', Georgia, serif",
      letterSpacing: "-0.035em",
      lineHeight: 1,
      fontSize: size,
      display: "inline-block",
    }}
  >
    <span style={{ color: "#1F2A7C" }}>Param</span>
    <span style={{ color: "#4D4D4D", fontSize: "1.12em" }}>OS</span>
  </span>
);
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FileText as FileDoc } from "lucide-react";

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { 
    online, 
    syncing, 
    pendingActions, 
    syncFailed,
    syncStatus,
    performSync,
    formatLastSync 
  } = useOffline();
  
  const [productionOpen, setProductionOpen] = useState(location.pathname.startsWith('/production') || user?.role === "production_user");
  
  // Sidebar collapse state for tablet responsiveness
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Auto-collapse sidebar on tablet screens
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Auto-collapse on tablet (768px - 1280px)
      if (width >= 768 && width < 1280) {
        setSidebarCollapsed(true);
      } else if (width >= 1280) {
        setSidebarCollapsed(false);
      }
      // Close mobile menu on resize
      if (width >= 768) {
        setMobileMenuOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Sidebar order — pre-Production section.
  // Excel Upload is intentionally NOT listed here; the /upload route still
  // exists and is reachable from the "Upload Excel" button on the Dashboard.
  const navItems = [
    // For admin, "/" now renders the Business Command Center (the redesigned
    // Business Dashboard). For staff/dispatch_user it still renders the
    // operational Dashboard.jsx. The label stays "Dashboard" so muscle memory
    // is preserved. (Legacy "Operations" sidebar entry removed per user
    // request; the /operations route itself is still registered in App.js so
    // any bookmarks keep working.)
    { path: "/", icon: LayoutDashboard, label: "Dashboard", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/lots", icon: Layers, label: "Lots", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/sales-orders", icon: ClipboardList, label: "Sales Orders", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/dispatch", icon: Truck, label: "Dispatch", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/dispatch-history", icon: History, label: "Dispatch History", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/goods-return", icon: RotateCcw, label: "Goods Return", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/roll-inventory", icon: Package, label: "Roll Inventory", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/stock-transfer", icon: ArrowRightLeft, label: "Stock Transfer", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/inventory-adjustment", icon: Scale, label: "Inventory Adjustment", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/damage-register", icon: AlertOctagon, label: "Damage Register", roles: ["Admin", "Staff", "dispatch_user"] },
    { path: "/audit-logs", icon: FileText, label: "Audit Logs", roles: ["Admin", "Staff", "dispatch_user"] },
    // The dedicated "/business-dashboard" route is retained for direct
    // navigation & existing bookmarks; it renders the same component that
    // now lives at "/" for admins.
    { path: "/business-dashboard", icon: TrendingUpIcon, label: "Business Dashboard", roles: ["Admin"] },
    { path: "/ai-advisor", icon: Brain, label: "AI Business Advisor", roles: ["Admin"] },
    { path: "/clients", icon: BarChart3, label: "Clients", roles: ["Admin"] },
    { path: "/lot-mapping", icon: Tag, label: "Lot Mapping", roles: ["Admin"] },
    // UX-003 (Phase 5): removed the duplicate "DB Maintenance" nav entry
    // that pointed at /database-maintenance. The canonical sidebar entry
    // for DB Maintenance is `/db-maintenance` (see postProductionNavItems
    // below). The `/database-maintenance` route remains registered in
    // App.js so existing bookmarks and any deep-links keep working.
  ];

  // Sidebar order — post-Production section (rendered after the Production block).
  const postProductionNavItems = [
    { path: "/user-management", icon: Users, label: "User Management", roles: ["Admin"] },
    { path: "/db-maintenance", icon: Database, label: "DB Maintenance", roles: ["Admin"] },
  ];
  
  const productionNavItems = [
    { path: "/production", icon: LayoutDashboard, label: "Overview", roles: ["Admin", "Staff", "production_user"] },
    { path: "/production/greige-stock", icon: Boxes, label: "Greige Stock / Inward", roles: ["Admin", "Staff", "production_user"] },
    { path: "/production/issue", icon: Truck, label: "Programme", roles: ["Admin", "Staff", "production_user"] },
    { path: "/production/lots", icon: ClipboardList, label: "Production Lots", roles: ["Admin", "Staff", "production_user"] },
    { path: "/production/lot-documents", icon: FileDoc, label: "Lot Documents", roles: ["Admin", "Staff", "production_user"] },
  ];

  // Check if user is production_user or dispatch_user (limited access)
  const isProductionUser = user?.role === "production_user";
  const isDispatchUser = user?.role === "dispatch_user";

  // For production_user, only show production module
  // For dispatch_user, only show dispatch-related items (no production)
  const filteredNavItems = isProductionUser ? [] : navItems.filter(item => {
    if (!item.roles.includes(user?.role)) return false;
    // Hide online-only items when offline
    if (item.requiresOnline && !online) return false;
    return true;
  });

  const filteredPostProductionNavItems = isProductionUser ? [] : postProductionNavItems.filter(item => {
    if (!item.roles.includes(user?.role)) return false;
    if (item.requiresOnline && !online) return false;
    return true;
  });
  
  const filteredProductionNavItems = productionNavItems.filter(item => {
    return item.roles.includes(user?.role);
  });

  // Production_user should always see production menu expanded
  // dispatch_user should NOT see production menu at all
  const showProductionMenu = !isDispatchUser && (isProductionUser || filteredProductionNavItems.length > 0);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleSync = async () => {
    if (online && (pendingActions > 0 || syncFailed)) {
      await performSync();
    }
  };

  // Get sync status indicator details
  const getSyncStatusDetails = () => {
    switch (syncStatus) {
      case SYNC_STATUS.ONLINE_SYNCED:
        return {
          icon: Check,
          label: "Online & Synced",
          color: "text-emerald-600",
          bgColor: "bg-emerald-50",
          borderColor: "border-emerald-200",
        };
      case SYNC_STATUS.OFFLINE_PENDING:
        return {
          icon: CloudOff,
          label: online ? `${pendingActions} Pending` : "Working offline — read-only mode",
          color: "text-amber-600",
          bgColor: "bg-amber-50",
          borderColor: "border-amber-200",
        };
      case SYNC_STATUS.SYNCING:
        return {
          icon: RefreshCw,
          label: "Syncing...",
          color: "text-blue-600",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
        };
      case SYNC_STATUS.SYNC_FAILED:
        return {
          icon: AlertTriangle,
          label: "Sync Failed",
          color: "text-red-600",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };
      default:
        return {
          icon: Cloud,
          label: "Unknown",
          color: "text-slate-600",
          bgColor: "bg-slate-50",
          borderColor: "border-slate-200",
        };
    }
  };

  const statusDetails = getSyncStatusDetails();
  const StatusIcon = statusDetails.icon;

  return (
    <TooltipProvider>
      {/* h-dvh = dynamic viewport height: excludes iOS Safari's collapsible
          toolbar / home-indicator so content never gets hidden behind chrome.
          h-screen kept as fallback for browsers that don't support dvh. */}
      <div className="flex h-screen h-dvh bg-slate-50 overflow-hidden">
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
        
        {/* Mobile Header */}
        <div className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-30 md:hidden">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-md"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img
              src={pfLogo}
              alt="Param Fabrics"
              draggable={false}
              className="select-none pointer-events-none"
              style={{ height: 22, width: "auto", objectFit: "contain" }}
            />
            <ParamOSWordmark size="16px" />
          </div>
          <div className={`w-2 h-2 rounded-full ${online ? 'bg-emerald-500' : 'bg-amber-500'}`} />
        </div>

        {/* Sidebar */}
        <aside 
          className={`
            fixed md:relative inset-y-0 left-0 z-50 md:z-auto
            bg-white border-r border-slate-200 flex flex-col
            transition-all duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            ${sidebarCollapsed ? 'md:w-16' : 'w-64'}
          `}
          data-testid="sidebar"
        >
          {/* Sidebar Header */}
          <div className={`p-4 ${sidebarCollapsed ? 'md:px-2' : 'p-6'} border-b border-slate-200`}>
            <div className="flex items-center justify-between">
              <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'md:justify-center md:w-full' : ''}`}>
                <img
                  src={pfLogo}
                  alt="Param Fabrics"
                  draggable={false}
                  className="select-none pointer-events-none flex-shrink-0"
                  style={{
                    height: sidebarCollapsed ? 28 : 34,
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
                {!sidebarCollapsed && (
                  <div className="md:block">
                    <ParamOSWordmark size="22px" />
                    <p
                      className="mt-1 tracking-wide"
                      style={{ fontSize: "11px", color: "#4D4D4D" }}
                    >
                      Built for Growth
                    </p>
                  </div>
                )}
              </div>
              {/* Mobile close button */}
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 md:hidden"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Collapse Toggle Button (visible on tablet/desktop) */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center shadow-sm hover:bg-slate-50 z-10"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <PanelLeft className="w-3 h-3 text-slate-600" />
            ) : (
              <PanelLeftClose className="w-3 h-3 text-slate-600" />
            )}
          </button>

          {/* Sync Status Banner */}
          {!sidebarCollapsed && (
            <div className="mx-4 mt-4" data-testid="sync-status-banner">
              <div className={`p-3 ${statusDetails.bgColor} border ${statusDetails.borderColor} rounded-md`}>
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 ${statusDetails.color}`}>
                    <StatusIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    <span className="text-sm font-medium">{statusDetails.label}</span>
                  </div>
                  {(syncStatus === SYNC_STATUS.OFFLINE_PENDING || syncStatus === SYNC_STATUS.SYNC_FAILED) && online && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleSync}
                      disabled={syncing}
                      className="h-7 px-2"
                      data-testid="sync-btn"
                    >
                      <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                </div>
                
                {/* Additional info */}
                {syncStatus === SYNC_STATUS.OFFLINE_PENDING && pendingActions > 0 && (
                  <p className="text-xs mt-1 opacity-75">
                    {online ? "Click to sync now" : "Will sync when connected"}
                  </p>
                )}
                {syncStatus === SYNC_STATUS.SYNC_FAILED && (
                  <p className="text-xs mt-1 opacity-75">
                    Click retry to try again
                  </p>
                )}
                {syncStatus === SYNC_STATUS.ONLINE_SYNCED && (
                  <p className="text-xs mt-1 opacity-75" data-testid="last-offline-sync">
                    Last Offline Sync: {formatLastSync()}
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Collapsed Sync Status Icon */}
          {sidebarCollapsed && (
            <div className="hidden md:flex justify-center mt-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={`p-2 rounded-md ${statusDetails.bgColor}`}>
                    <StatusIcon className={`w-4 h-4 ${statusDetails.color} ${syncing ? 'animate-spin' : ''}`} />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {statusDetails.label}
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          <nav className={`flex-1 p-4 ${sidebarCollapsed ? 'md:p-2' : ''} space-y-1 overflow-auto`} data-testid="main-nav">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== "/" && location.pathname.startsWith(item.path));
              
              return sidebarCollapsed ? (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      className={`hidden md:flex items-center justify-center p-2.5 rounded-md transition-colors ${
                        isActive 
                          ? "bg-slate-900 text-white" 
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
                    isActive 
                      ? "bg-slate-900 text-white" 
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium text-sm">{item.label}</span>
                  {item.requiresOnline && (
                    <Wifi className="w-3 h-3 ml-auto opacity-50" />
                  )}
                </NavLink>
              );
            })}
            
            {/* Production Module Section - Always show for production_user */}
            {showProductionMenu && (
              sidebarCollapsed ? (
                // Collapsed Production Menu
                <div className="hidden md:block mt-4 pt-4 border-t border-slate-200">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          setSidebarCollapsed(false);
                          setProductionOpen(true);
                        }}
                        className={`w-full flex items-center justify-center p-2.5 rounded-md transition-colors ${
                          location.pathname.startsWith('/production')
                            ? "bg-indigo-600 text-white" 
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                      >
                        <Factory className="w-5 h-5" strokeWidth={1.5} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Production
                    </TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <Collapsible open={productionOpen || isProductionUser} onOpenChange={isProductionUser ? undefined : setProductionOpen} className="mt-4">
                  <CollapsibleTrigger 
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
                      isProductionUser 
                        ? "bg-indigo-50 text-indigo-700 cursor-default" 
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                    disabled={isProductionUser}
                  >
                    <Factory className="w-5 h-5" strokeWidth={1.5} />
                    <span className="font-medium text-sm flex-1 text-left">Production</span>
                    {!isProductionUser && (productionOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-1 mt-1">
                    {filteredProductionNavItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-3 px-4 py-2 rounded-md transition-colors ${
                            isActive 
                              ? "bg-indigo-600 text-white" 
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                          }`}
                          data-testid={`nav-production-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className="w-4 h-4" strokeWidth={1.5} />
                          <span className="font-medium text-sm">{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              )
            )}

            {/* Post-Production items (User Management, DB Maintenance system/admin).
                 Rendered AFTER the Production block per the sidebar spec so the
                 final admin items sit at the bottom of the nav. */}
            {filteredPostProductionNavItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== "/" && location.pathname.startsWith(item.path));

              return sidebarCollapsed ? (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      className={`hidden md:flex items-center justify-center p-2.5 rounded-md transition-colors ${
                        isActive
                          ? "bg-slate-900 text-white"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium text-sm">{item.label}</span>
                  {item.requiresOnline && (
                    <Wifi className="w-3 h-3 ml-auto opacity-50" />
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* User Section */}
          <div className={`p-4 ${sidebarCollapsed ? 'md:p-2' : ''} border-t border-slate-200`}>
            {/* Connection Status - only show expanded */}
            {!sidebarCollapsed && (
              <div className="flex items-center justify-between mb-3 px-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`flex items-center gap-1.5 text-xs ${online ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {online ? (
                        <>
                          <Wifi className="w-3 h-3" />
                          <span>Online</span>
                        </>
                      ) : (
                        <>
                          <WifiOff className="w-3 h-3" />
                          <span>Offline</span>
                        </>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {online 
                      ? "Connected to server" 
                      : "Working offline — read-only mode. Writes require an internet connection."}
                  </TooltipContent>
                </Tooltip>
                
                {pendingActions > 0 && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                    {pendingActions} pending
                  </Badge>
                )}
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`w-full flex items-center gap-3 ${sidebarCollapsed ? 'md:justify-center md:p-2' : 'px-3 py-2'} rounded-md hover:bg-slate-100 transition-colors text-left`} 
                  data-testid="user-menu"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isAdmin ? 'bg-amber-100' : 'bg-slate-100'}`}>
                    {isAdmin ? (
                      <Shield className="w-4 h-4 text-amber-600" />
                    ) : (
                      <User className="w-4 h-4 text-slate-600" />
                    )}
                  </div>
                  {!sidebarCollapsed && (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className={`text-xs ${isAdmin ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50'}`}>
                          {user?.role}
                        </Badge>
                      </div>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="change-password-link">
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {!sidebarCollapsed && (
              <p className="text-xs text-slate-400 text-center mt-3">
                ParamOS • Designed for Modern Textile Businesses
              </p>
            )}
          </div>
        </aside>

        {/* Main Content.
            <main> fills the dvh-bounded viewport and is a flex column. The
            inner overflow-auto div is the *page* scroll container. The padded
            wrapper uses min-h-full so:
              • Regular pages: grow with their content → outer container scrolls.
              • Viewport-fit pages (e.g. AI Advisor): set h-full + flex-col on
                their root and own their internal scroll, so the bottom never
                hides behind iOS Safari chrome on iPad. */}
        <main className="flex-1 min-h-0 overflow-hidden pt-14 md:pt-0 flex flex-col">
          <div className="flex-1 min-h-0 overflow-auto">
            <div className="p-3 sm:p-4 lg:p-6 w-full max-w-full lg:max-w-7xl mx-auto min-h-full flex flex-col">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
